import { randomUUID } from '@ag-ui/client';
import type { AgentSubscriber, AssistantMessage, HttpAgent, Message } from '@ag-ui/client';
import { AG_UI_ROLE } from '../agent/ag-ui.constants';
import { toMessageCards } from '../messages/doc-answer';
import type { DocAnswerCard } from '../messages/doc-answer';
import { trailingFallbackOffer } from '../messages/hub-offer';
import { SEARCH_ANSWER_STATUS, STALL_TIMEOUT_MS } from './search-answer.constants';
import type { SearchAnswerStatus } from './search-answer.constants';

/**
 * The run's second channel (STATE_SNAPSHOT, then RFC-6902 STATE_DELTA patches),
 * where the pipeline puts everything that isn't the answer text. AG-UI types it
 * as `any`, so this is the shape panda-service actually sends.
 */
export interface SearchAnswerState {
  /** Keys feedback on an answer that has no message row. */
  trace_id?: string;
  show_chat?: boolean;
}

// React-free so the fail-safe paths (stall, abort, mid-stream failure,
// finished-empty) can be tested directly — `shared` has no @testing-library to
// render a hook with, as ../citations/chunks.hooks.

/** `threadId` and `runId` are client-minted (the endpoint persists no thread); `runId` is what a continuing chat sends as `parentRunId` to join the Langfuse sessions. */
export interface SearchAnswerSnapshot {
  status: SearchAnswerStatus;
  cards: DocAnswerCard[];
  /** Live references, carried raw so the chat handoff can pass them on as-is. */
  messages: readonly Message[];
  traceId: string | null;
  threadId: string;
  runId: string;
}

export interface SearchAnswerRunOptions {
  agent: HttpAgent;
  term: string;
  onChange: (snapshot: SearchAnswerSnapshot) => void;
  stallTimeoutMs?: number;
}

export interface SearchAnswerRun {
  /** Resolves with the status the run ended in. Never rejects. */
  readonly settled: Promise<SearchAnswerStatus>;
  abort(): void;
  /**
   * Per token and unthrottled. AG-UI only notifies the subscriber captured when
   * the run started, so the chat — which adopts a run already in flight — would
   * otherwise get nothing until it ends. Returns an unsubscribe.
   */
  subscribeMessages(listener: () => void): () => void;
  /** The last snapshot emitted; what a card adopting the run shows first. */
  latest(): SearchAnswerSnapshot;
  /** Every snapshot from now on, throttled by nobody. Returns an unsubscribe. */
  subscribe(listener: (snapshot: SearchAnswerSnapshot) => void): () => void;
}

const isAssistant = (message: Message): message is AssistantMessage =>
  message.role === AG_UI_ROLE.ASSISTANT;

function toCards(messages: readonly Message[]): DocAnswerCard[] {
  return messages
    .filter(isAssistant)
    .flatMap(message => toMessageCards({ toolCalls: message.toolCalls }));
}

const hasAnswerText = (cards: DocAnswerCard[]) => cards.some(card => Boolean(card.text.trim()));

/** Returns immediately; progress arrives via `onChange`. */
export function startSearchAnswerRun({
  agent,
  term,
  onChange,
  stallTimeoutMs = STALL_TIMEOUT_MS,
}: SearchAnswerRunOptions): SearchAnswerRun {
  let status: SearchAnswerStatus = SEARCH_ANSWER_STATUS.LOADING;
  let cards: DocAnswerCard[] = [];
  let messages: readonly Message[] = [];
  let traceId: string | null = null;
  let cancelled = false;
  // The run reached ERROR/DONE. Events buffered before `onStall` aborted still
  // land afterwards, and without this they rearm the stall timer and repaint
  // the card past the terminal state we already showed.
  let terminal = false;
  // Distinguishes our own abort from a real stream failure: aborting can reject
  // the run promise, and that rejection must not be reported as an error.
  let aborted = false;
  let stallTimer: ReturnType<typeof setTimeout> | undefined;
  // External per-token listeners (the chat, after a live handoff). Kept apart
  // from `onChange` so they fire raw and unthrottled off every message change.
  const messageListeners = new Set<() => void>();
  const changeListeners = new Set<(snapshot: SearchAnswerSnapshot) => void>();
  const runId = randomUUID();
  let latest: SearchAnswerSnapshot = {
    status,
    cards,
    messages,
    traceId,
    threadId: agent.threadId,
    runId,
  };

  const emit = () => {
    if (cancelled) return;
    latest = { status, cards, messages, traceId, threadId: agent.threadId, runId };
    onChange(latest);
    changeListeners.forEach(listener => listener(latest));
  };

  const stopped = () => cancelled || terminal;

  const clearStall = () => {
    if (stallTimer !== undefined) clearTimeout(stallTimer);
    stallTimer = undefined;
  };

  const failWith = () => {
    if (stopped()) return;
    clearStall();
    terminal = true;
    status = SEARCH_ANSWER_STATUS.ERROR;
    emit();
  };

  // Terminal, so the RUN_FINISHED or RUN_ERROR that follows is ignored — a
  // hidden run must not flip to the error line.
  const hide = () => {
    if (stopped()) return;
    clearStall();
    terminal = true;
    status = SEARCH_ANSWER_STATUS.HIDDEN;
    cards = [];
    emit();
  };

  const onStall = () => {
    stallTimer = undefined;
    aborted = true;
    agent.abortRun();
    failWith();
  };

  const restartStall = () => {
    if (stopped()) return;
    clearStall();
    stallTimer = setTimeout(onStall, stallTimeoutMs);
  };

  const applyState = (state: SearchAnswerState | undefined) => {
    if (stopped()) return;
    restartStall();
    // Trace id first: the hide is reported with it, and it has to survive being
    // the last thing this run says.
    const nextTraceId = state?.trace_id ?? traceId;
    const traceChanged = nextTraceId !== traceId;
    traceId = nextTraceId;
    // Only an explicit `false` hides — an absent field is undecided, and the
    // pipeline may set it in a later patch.
    if (state?.show_chat === false) {
      hide();
      return;
    }
    if (traceChanged) emit();
  };

  const applyMessages = (next: readonly Message[]) => {
    if (stopped()) return;
    restartStall();
    messages = next;
    cards = toCards(next);
    if (hasAnswerText(cards)) {
      status = SEARCH_ANSWER_STATUS.STREAMING;
    }
    emit();
    messageListeners.forEach(listener => listener());
  };

  const finish = () => {
    if (stopped()) return;
    clearStall();
    if (!hasAnswerText(cards)) {
      // The backend emits the offer before closing the stream, so it's already in `messages` here.
      if (trailingFallbackOffer(messages)) {
        terminal = true;
        status = SEARCH_ANSWER_STATUS.SUGGESTION;
        emit();
        return;
      }
      hide();
      return;
    }
    terminal = true;
    status = SEARCH_ANSWER_STATUS.DONE;
    emit();
  };

  const fail = () => {
    if (stopped() || aborted) return;
    failWith();
  };

  const subscriber: AgentSubscriber = {
    onStateChanged: ({ state }) => applyState(state),
    onMessagesChanged: ({ messages }) => applyMessages(messages),
    onRunFinishedEvent: finish,
    onRunErrorEvent: fail,
  };

  const endedMidRun = () =>
    status === SEARCH_ANSWER_STATUS.LOADING || status === SEARCH_ANSWER_STATUS.STREAMING;

  agent.addMessage({ id: randomUUID(), role: AG_UI_ROLE.USER, content: term });
  emit();
  restartStall();

  const settled = (async () => {
    try {
      await agent.runAgent({ runId }, subscriber);
      // A stream that ends without a terminal event is a drop, not a success.
      if (endedMidRun()) failWith();
    } catch {
      fail();
    } finally {
      clearStall();
    }
    return status;
  })();

  return {
    settled,
    abort: () => {
      if (cancelled) return;
      cancelled = true;
      aborted = true;
      clearStall();
      agent.abortRun();
    },
    subscribeMessages: listener => {
      messageListeners.add(listener);
      return () => messageListeners.delete(listener);
    },
    latest: () => latest,
    subscribe: listener => {
      changeListeners.add(listener);
      return () => changeListeners.delete(listener);
    },
  };
}
